import { useEffect, useMemo, useRef, useState } from "react"
import type { RefObject } from "react"
import { useField } from "formik"

import { ProductGroups } from "../../../../../modules/merchant/admin/products/productsTypes"

import { DeliveryTimesField, DynamicConfigKey } from "../form/formTypes"
import { MeetingServicePriceListState } from "../../createOrderTypes"

import { buildPriceMap, collectSelectedLines, hasPriceDiff, updatePricesInDeliveries } from "./product/helpers"
import { PriceMismatchDialog } from "./product/PriceMismatchDialog"
import { CreditNoteOverlay } from "./product/CreditNoteOverlay"
import { ProductContainer } from "./product/ProductContainer"

import { ShopFiltersTypes } from "./ShopList"

type ShopListContentProps = Readonly<{
    productsList?: ProductGroups[]
    filters: ShopFiltersTypes
    searchRegex?: RegExp
    orderId?: number
    isRefundOrder: boolean
    handleCloseMainDialog: () => void
    userId: number
    isEditMode?: boolean
    scrollContainerRef?: RefObject<HTMLDivElement>
}>

export function ShopListContent({
    productsList,
    searchRegex,
    filters,
    orderId,
    isRefundOrder,
    handleCloseMainDialog,
    userId,
    isEditMode,
    scrollContainerRef,
}: ShopListContentProps) {
    const [{ value: deliveries }, , deliveriesHelpers] = useField<DeliveryTimesField>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )
    const [{ value: priceListItem }] = useField<MeetingServicePriceListState>("priceList")

    const [showPriceWarning, setShowPriceWarning] = useState(false)
    const warnedRef = useRef(false)

    const priceListIncludeVat = !!priceListItem?.item?.priceListIncludeVat
    const priceCompareOptions = useMemo(() => ({ priceListIncludeVat }), [priceListIncludeVat])

    const priceById = useMemo(
        () => buildPriceMap(productsList, priceCompareOptions),
        [productsList, priceCompareOptions],
    )
    const selectedLines = useMemo(() => collectSelectedLines(deliveries), [deliveries])
    const diff = useMemo(() => hasPriceDiff(priceById, selectedLines), [priceById, selectedLines])

    useEffect(() => {
        if (warnedRef.current) return
        if (!productsList || productsList.length === 0) return
        if (selectedLines.length === 0) return
        if (!diff) return

        warnedRef.current = true
        setShowPriceWarning(true)
    }, [productsList, selectedLines.length, diff])

    const handleCloseDialog = () => {
        setShowPriceWarning(false)
    }

    const handleChangePrices = () => {
        if (!deliveries || !Array.isArray(deliveries.deliveryTimes)) {
            handleCloseDialog()
            return
        }

        const { updated, changed } = updatePricesInDeliveries(deliveries, priceById)
        if (changed) {
            deliveriesHelpers.setValue(updated)
        }
        handleCloseDialog()
    }

    return (
        <>
            {isRefundOrder ? (
                <CreditNoteOverlay orderId={orderId} />
            ) : (
                <ProductContainer
                    list={productsList}
                    searchRegex={searchRegex}
                    isListView={filters.isListView}
                    isFavoriteFilter={filters.isFavorite}
                    isRefundOrder={isRefundOrder}
                    userId={userId}
                    isEditMode={isEditMode}
                    scrollContainerRef={scrollContainerRef}
                />
            )}
            {showPriceWarning && (
                <PriceMismatchDialog
                    open={showPriceWarning}
                    handleCloseDialog={handleCloseDialog}
                    handleCloseMainDialog={handleCloseMainDialog}
                    handleChangePrices={handleChangePrices}
                    isRefundOrder={isRefundOrder}
                />
            )}
        </>
    )
}
